// HowItWorksHub — six seats + honest mechanics.
// Self-sufficient: every string rides t() with inline defaults (zero required
// locale entries). Steps speak the real, shipped flows. Accordion mechanics
// answer the cross-cutting questions that generate support load.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Users, GraduationCap, School, Heart, Globe, Church, ChevronDown, User, Building2,
} from 'lucide-react'

// ── Tokens: platform dark family ────────────────────────────────────────────
const M = '#06060c'
const D2 = '#0a0a14'
const CARD = '#0d0d15'
const BLUE = '#0ea5e9'
const G = '#d4a843'
const BR = 'rgba(255,255,255,0.10)'
const TX = '#ffffff'
const MU = 'rgba(255,255,255,0.55)'
const MU2 = 'rgba(255,255,255,0.35)'
const serif = "'Playfair Display', Georgia, serif"

type SeatId = 'parent' | 'student' | 'individual' | 'teacher' | 'org' | 'sponsor' | 'ngo' | 'church'

interface Step { title: string; desc: string }
interface Seat {
  id: SeatId
  label: string
  icon: React.ReactNode
  color: string
  intro: string
  steps: Step[]
}

const SEATS: Seat[] = [
  {
    id: 'parent',
    label: 'Parent',
    icon: <Users size={16} />,
    color: G,
    intro: 'Your dashboard is the home base: every child, every subject, every sign of progress, updated live.',
    steps: [
      { title: 'See what they did today', desc: 'Lessons, quiz scores, mastered concepts, and the teacher\u2019s written feedback, updated live on your dashboard.' },
      { title: 'Follow the mastery graph', desc: 'Not completion, understanding: a rolling score of what your child has genuinely learned, blended 60/40 from quiz history.' },
      { title: 'Read the AI\u2019s profile of your child', desc: 'Strengths, growth edges, learning pace, written in plain language from their actual answers. Updated as they learn.' },
      { title: 'Add your own studies', desc: 'Parents study too: add yourself at half price with any active family subscription, or full price on your own.' },
      { title: 'Keep the record', desc: 'Printable transcripts and learning records your child carries to any school, employer, or university.' },
    ],
  },
  {
    id: 'student',
    label: 'Student',
    icon: <GraduationCap size={16} />,
    color: BLUE,
    intro: 'One tutor, all yours. It teaches in conversation, checks as you go, and never moves on before you are ready.',
    steps: [
      { title: 'Learn by talking', desc: 'Lessons are conversations, not videos. Your AI tutor explains one concept at a time and asks you a question at the end of every message.' },
      { title: 'Answer with a tap, or in your own words', desc: 'Quick-checks give you answer chips; deeper questions ask you to explain. Both count.' },
      { title: 'Take the quiz, own the retry', desc: 'Quizzes are generated from the exact concepts your lesson taught. Miss the pass mark and you review the weak concepts, then retake. Retakes are free and unpenalised; that is the design, not a flaw.' },
      { title: 'See yourself get better', desc: 'Your mastery score climbs with every passed lesson, and your written profile grows: what you are good at, and what is next.' },
      { title: 'Earn the record', desc: 'Everything you do becomes a printable transcript. Your work counts, even outside a school.' },
    ],
  },
  {
    id: 'individual',
    label: 'Individual Learner',
    icon: <User size={16} />,
    color: BLUE,
    intro: 'No children, no school, no committee. Just you, a personal AI tutor, and the full catalog.',
    steps: [
      { title: 'Join as yourself', desc: 'From any landing page, choose "Join as an individual". One account, no family hub, straight to learning in three steps.' },
      { title: 'Pick any subject', desc: 'Browse the full catalog, standard and elite, and add whatever you want to learn. Your first subject is free to try with the full AI tutoring included.' },
      { title: 'The same tutor, tuned to you', desc: 'The AI teaches at your age and pace, asks questions every message, and quizzes you on exactly what was covered. Retakes are free; that is the design.' },
      { title: 'Study at your own rhythm', desc: 'No classes, no schedule. The platform remembers exactly where you stopped, on every subject, and mastery is measured by understanding, not attendance.' },
      { title: 'Credentials that are yours', desc: 'Cross 90% mastery in any subject and a verified certificate issues automatically. A public verification page any employer can check, with your PIN controlling how much they see.' },
    ],
  },
  {
    id: 'org',
    label: 'Organisation',
    icon: <Building2 size={16} />,
    color: BLUE,
    intro: 'Your own learning platform, branded as yours, powered by our engine. Universities, businesses, NGOs, churches, and clubs run on the same spine.',
    steps: [
      { title: 'Create your platform in minutes', desc: 'Pick your organisation type, name it, upload your logo, and claim your address: yourname.stillwatersacademy.com. Your portal is live before your coffee cools.' },
      { title: 'The platform speaks your language', desc: 'A business sees Course Managers, Employees, and Training Modules. A church sees Pastors, Members, and Studies. Every screen adapts to what your organisation actually is.' },
      { title: 'Invite your people', desc: 'Staff and learners receive a magic link and land signed straight into your branded portal. Nobody types a password on day one.' },
      { title: 'Build your curriculum', desc: 'Generate a custom course by describing it, adopt any course from the audited catalog, or attach a picture to any concept so learners see exactly what is being taught.' },
      { title: 'Seats, simple', desc: 'One price per learner per month, teachers and admins free. Buy ten, invite ten, need an eleventh, buy one more. Staff can request more seats and the request reaches your administrator by email.' },
      { title: 'Evidence under your name', desc: 'Mastery scores, verifiable credentials, printable records, everything issued under YOUR organisation\u2019s name. Remove a learner and their seat frees instantly for the next person.' },
      { title: 'Your money, your controls', desc: 'The billing portal shows every invoice, past and present. Adjust seats, update cards, cancel anytime; your people keep access to the end of what you paid for.' },
    ],
  },
  {
    id: 'teacher',
    label: 'Teacher & Principal',
    icon: <School size={16} />,
    color: BLUE,
    intro: 'The AI runs the lessons. You run the judgment. The system keeps you in command of every assessment.',
    steps: [
      { title: 'Pick the courses from the catalog', desc: 'Choose from 114+ audited courses, clone a colleague\u2019s shared course free from the community library, or generate a custom one.' },
      { title: 'Invite your teachers, scoped', desc: 'Each teacher lands on their own classes only: rosters, progress, and risk flags, nothing else. Principals see the whole school.' },
      { title: 'Watch the whole school', desc: 'One table shows mastery, days since active, and who is struggling, for every student in every class. Click any student to drill down.' },
      { title: 'Get notified, not surprised', desc: 'Quiz passed, quiz failed, essay graded, student stalled: the bell tells you the moment it happens, in real time.' },
      { title: 'Own the grade', desc: 'Your written feedback and overrides land on the official record. The AI score stays; your judgment sits beside it, and parents see both.' },
      { title: 'Print real records', desc: 'Transcripts and learning records a parent or an inspector can hold, generated from live evidence.' },
    ],
  },
  {
    id: 'sponsor',
    label: 'Sponsor',
    icon: <Heart size={16} />,
    color: G,
    intro: 'Two ways to fund a child, both ending in the same place: their whole education, and your front-row seat to it.',
    steps: [
      { title: 'Choose your mode', desc: 'Pool: your seats go to verified children with the greatest need; you watch anonymised progress. Named: you pick the child, a nephew, a housekeeper\u2019s daughter, a family from your church, and we onboard them directly.' },
      { title: 'Nominate (named mode)', desc: 'From your dashboard, enter the child\u2019s name and their guardian\u2019s email. The system sends the invitation and links them to your seat.' },
      { title: 'They redeem and start learning', desc: 'The guardian clicks the email, signs in, and the child\u2019s full academy unlocks: standard, elite, and Bible courses, nothing held back.' },
      { title: 'Watch their journey', desc: 'Your dashboard shows their lessons, quiz scores, and mastery as a live graph. Anonymised or named; your choice at checkout, their dignity either way.' },
      { title: 'Adjust anytime', desc: 'Add seats mid-subscription, or cancel. If you cancel, the child keeps learning to the end of what you paid for, then their seat passes to a new sponsor. No cliff.' },
      { title: 'One-time gifts work too', desc: 'A single gift buys exactly the months you purchased: one, six, or twelve. It lands in the pool and is assigned within days.' },
    ],
  },
  {
    id: 'ngo',
    label: 'NGO Partner',
    icon: <Globe size={16} />,
    color: '#14b8a6',
    intro: 'Bring education to the families you serve, through your own portal, without waiting on anyone.',
    steps: [
      { title: 'Apply, and you are in', desc: 'The application is instant. Sign up with the same email and you land straight on your partner dashboard, ready to work.' },
      { title: 'Nominate your learners', desc: 'Add each child\u2019s name and guardian email yourself. They appear as pending until a seat reaches them.' },
      { title: 'Get seats two ways', desc: 'Request sponsored seats and our team assigns them, or purchase your own at the matched rate and they arrive the moment you pay.' },
      { title: 'Activate each child', desc: 'When a seat is funded, press Activate beside the child\u2019s name. The invitation goes to the guardian, the child\u2019s academy unlocks.' },
      { title: 'Track everyone', desc: 'Your dashboard shows every child\u2019s subjects, mastery, quiz average, and last activity. Group view, individual drill-down, capacity bar.' },
    ],
  },
  {
    id: 'church',
    label: 'Church Leader',
    icon: <Church size={16} />,
    color: '#f59e0b',
    intro: 'A Bible College for your whole congregation, taught verse by verse, with your oversight on everything.',
    steps: [
      { title: 'Register your church, free', desc: 'Your church hub and pastor dashboard cost nothing. Seats are $7 a month per family, assigned or sponsored as you choose.' },
      { title: 'The catalog is ready', desc: '31 courses across Bible journey, theology, ministry, ethics, and church history. Two courses are free forever for anyone.' },
      { title: 'Invite your congregation', desc: 'Members join through your link or invitation. Their progress, subjects, and spiritual growth show on your dashboard.' },
      { title: 'Teaching you can trust', desc: 'Every lesson teaches the text faithfully, verse by verse, from an orthodox evangelical framework. You see what every member studies.' },
      { title: 'Sponsor seats for families in need', desc: 'Assign funded seats to members who cannot pay, the same sponsorship engine the rest of the platform runs on.' },
    ],
  },
]

// ── The honest mechanics: cross-cutting answers ─────────────────────────────
const MECHANICS: { q: string; a: string }[] = [
  {
    q: 'Mastery vs. completion: what is the difference?',
    a: 'Completion means you reached the end. Mastery means the system believes you understood: quizzes are scored against the server\u2019s own answer key, 80% passes, and your rolling score blends history so one bad day does not erase good work. A child can complete a course without mastering it; they cannot master it without learning it.',
  },
  {
    q: 'What does the AI do, and what does the teacher do?',
    a: 'The AI tutor teaches every lesson, one-on-one, adapting vocabulary and pace to the learner\u2019s age. Teachers and parents hold the judgment layer: written feedback, grade overrides that sit beside (never erase) the AI score, encouragement, and the decision about what to study next. The AI never has the final word on a child\u2019s record.',
  },
  {
    q: 'Who can see a sponsored child\u2019s data?',
    a: 'Sponsors see progress snapshots only: lessons, scores, mastery. Never application reasons, never identity, unless the sponsor chose named mode and the family consented. NGOs see their own nominated children. Teachers see their own classes. Privacy is enforced in the database layer, not by policy alone: each role\u2019s queries are physically scoped to what they own.',
  },
  {
    q: 'What happens if a sponsor cancels?',
    a: 'The child keeps full access to the end of the period the sponsor paid for, then their seat passes to another sponsor from the pool. Their learning record is never deleted; a new sponsor simply picks up the same child, and the graph keeps climbing.',
  },
  {
    q: 'Can we print official records?',
    a: 'Yes. Learning records, transcripts with period filtering, and curriculum outlines all print cleanly. Schools get teacher and principal signature blocks; homeschools get parent-as-teacher-of-record; churches get pastor sign-off. The evidence behind every line is the child\u2019s actual lesson and quiz history.',
  },
    {
    q: 'What does a white-label organisation actually get?',
    a: 'Their own web address with their logo and colours, their terminology on every screen, their staff and learners with scoped access, their curriculum (generated, adopted from our catalog, or both), credentials issued under their name, per-seat billing with free teachers and admins, and a billing portal with every invoice. The engine, assessment, and evidence layer underneath is ours and stays maintained; the brand their learners see is entirely theirs.',
  },
  {
    q: 'How does the AI handle age and language?',
    a: 'Every learner\u2019s age is resolved once, from their profile, and both the teaching and the quizzes adapt to it: the same concept is taught very differently to a 10-year-old and a 30-year-old. Lessons run in the interface language you choose; the tutor switches with you.',
  },
]

export default function HowItWorksHub() {
  const { t } = useTranslation()
  const [activeSeat, setActiveSeat] = useState<SeatId>('parent')
  const [openMech, setOpenMech] = useState<number | null>(null)

  const seat = SEATS.find(s => s.id === activeSeat)!

  return (
    <div style={{ background: M, fontFamily: "'Inter', system-ui, sans-serif", color: TX }}>
      {/* ═══ SEAT TABS ═══ */}
      <section style={{ padding: '48px 20px 8px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            {SEATS.map(s => {
              const isActive = activeSeat === s.id
              return (
                <button
                  key={s.id}
                  onClick={() => setActiveSeat(s.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 22px', borderRadius: 999, cursor: 'pointer',
                    border: `1.5px solid ${isActive ? s.color : BR}`,
                    background: isActive ? `${s.color}18` : 'transparent',
                    color: isActive ? s.color : MU,
                    fontWeight: 800, fontSize: 13,
                    transition: 'all 0.2s',
                  }}
                >
                  {s.icon}
                  {t(`hiw.seats.${s.id}`, s.label)}
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* ═══ THE WALKTHROUGH ═══ */}
      <section style={{ padding: '24px 20px 56px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSeat}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
            >
              {/* Seat intro */}
              <p style={{ textAlign: 'center', color: MU, fontSize: 15.5, lineHeight: 1.7, maxWidth: 640, margin: '0 auto 36px' }}>
                {t(`hiw.intro.${activeSeat}`, seat.intro)}
              </p>

              {/* Steps */}
              <div style={{ background: CARD, border: `1px solid ${BR}`, borderRadius: 24, padding: 'clamp(20px, 5vw, 36px)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 230px), 1fr))', gap: 'clamp(20px, 4vw, 28px)' }}>
                  {seat.steps.map((step, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column' }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 12,
                        background: `linear-gradient(135deg, ${seat.color}, ${seat.color}cc)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: M, fontSize: 15, fontWeight: 900, marginBottom: 12,
                        boxShadow: `0 6px 16px -5px ${seat.color}66`,
                      }}>
                        {idx + 1}
                      </div>
                      <h3 style={{ fontSize: 15.5, fontWeight: 800, margin: '0 0 8px', color: TX, lineHeight: 1.35 }}>
                        {t(`hiw.${activeSeat}.s${idx + 1}t`, step.title)}
                      </h3>
                      <p style={{ fontSize: 13.5, color: MU, lineHeight: 1.65, margin: 0 }}>
                        {t(`hiw.${activeSeat}.s${idx + 1}d`, step.desc)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ═══ HONEST MECHANICS ═══ */}
      <section style={{ padding: '24px 20px 72px', background: D2 }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <p style={{ fontSize: 11, color: BLUE, fontWeight: 800, letterSpacing: '0.25em', textTransform: 'uppercase', margin: '0 0 12px' }}>
              {t('hiw.mech.eyebrow', 'The honest mechanics')}
            </p>
            <h2 style={{ fontFamily: serif, fontSize: 'clamp(1.7rem, 4vw, 2.4rem)', fontWeight: 700, margin: 0, color: TX }}>
              {t('hiw.mech.title', 'Questions the system owes you answers to')}
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {MECHANICS.map((m, i) => {
              const open = openMech === i
              return (
                <div key={i} style={{ background: CARD, border: `1px solid ${open ? `${BLUE}44` : BR}`, borderRadius: 16, overflow: 'hidden', transition: 'border-color 0.2s' }}>
                  <button
                    onClick={() => setOpenMech(open ? null : i)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '18px 22px',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
                      color: TX, fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: 800, fontSize: 14.5, lineHeight: 1.4 }}>{t(`hiw.mech.q${i + 1}`, m.q)}</span>
                    <ChevronDown size={16} color={MU} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>
                  <AnimatePresence>
                    {open && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22 }} style={{ overflow: 'hidden' }}>
                        <p style={{ padding: '0 22px 20px', color: MU, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
                          {t(`hiw.mech.a${i + 1}`, m.a)}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>

          <p style={{ textAlign: 'center', color: MU2, fontSize: 12, margin: '32px 0 0' }}>
            {t('hiw.mech.note', 'Still stuck? support@stillwatersacademy.com answers within a day.')}
          </p>
        </div>
      </section>
    </div>
  )
}